import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  KatanaPathElementDetail,
  KatanaPathDetailsResponse,
  KatanaPathDumpRow,
  KatanaPathDumpView,
  KatanaPathSummary,
  KatanaTSP,
  KatanaTranslationPreviewRow,
} from '@/types';
import { BelowMapSetter } from '../layouts/BelowMapContext';
import { BelowMapTab } from '../layouts/BelowMapTab';
import {
  useGetKatanaPathDumpQuery,
  useGetKatanaPointsQuery,
  useGetKatanaTSPsQuery,
  useLazyGetKatanaPathDetailsQuery,
  useLazyGetKatanaTranslationPreviewQuery,
} from '../services/edgeApi';
import { PointSelectionModal } from './katanaExplorer/PointSelectionModal';
import { ResultsTable } from './katanaExplorer/ResultsTable';
import { PathSummaryResults } from './katanaExplorer/PathSummaryResults';
import { PointSummary } from './katanaExplorer/PointSummary';
import { ColumnSettings } from './katanaExplorer/ColumnSettings';
import { PathingStatusTab } from './katanaExplorer/PathingStatusTab';
import { ExportToPathReportModal } from './katanaExplorer/ExportToPathReportModal';
import { TranslationPreviewModal } from './katanaExplorer/TranslationPreviewModal';
import {
  columns,
  defaultLimit,
} from './katanaExplorer/constants';
import { useKatanaExplorerFilters } from './katanaExplorer/useKatanaExplorerFilters';

const viewModeOptions: Array<{
  value: KatanaPathDumpView;
  label: string;
  helpText: string;
}> = [
  {
    value: 'summary',
    label: 'Summary',
    helpText: 'One result per path with high-level fields.',
  },
  {
    value: 'rows',
    label: 'Rows',
    helpText: 'Combined path and row table.',
  },
];

const isRowResult = (
  result: KatanaPathDumpRow | KatanaPathSummary,
): result is KatanaPathDumpRow =>
  typeof result.link_order === 'number';

const isSummaryResult = (
  result: KatanaPathDumpRow | KatanaPathSummary,
): result is KatanaPathSummary => !isRowResult(result);

const toResultKey = (
  result: KatanaPathDumpRow | KatanaPathSummary,
  fallbackIndex: number,
): string => {
  if (isRowResult(result)) {
    const rowPathId = result.id ?? result.path_id ?? 'unknown';
    return [
      'row',
      result.gas_date,
      rowPathId,
      result.link_order,
      result.nom_tsp_name ?? '',
      result.nom_r_point_name ?? '',
      result.nom_d_point_name ?? '',
    ].join('|');
  }

  return [
    'summary',
    result.source_path_id ?? '',
    result.id ?? '',
    result.translation_group_key ?? '',
    result.original_path_key ?? '',
    result.original_path_fingerprint ?? '',
    result.route_list ?? '',
    result.r_point_name ?? '',
    result.d_point_name ?? '',
    fallbackIndex,
  ].join('|');
};

const mergeUniqueResults = (
  current: Array<KatanaPathDumpRow | KatanaPathSummary>,
  incoming: Array<KatanaPathDumpRow | KatanaPathSummary>,
) => {
  const merged = [...current];
  const existingKeys = new Set(
    current.map((result, index) => toResultKey(result, index)),
  );

  incoming.forEach((result, index) => {
    const key = toResultKey(result, index);
    if (!existingKeys.has(key)) {
      existingKeys.add(key);
      merged.push(result);
    }
  });

  return merged;
};

const normalizeDeskNameValue = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (Array.isArray(value)) {
    const values = value
      .map((item) => (typeof item === 'string' ? item.trim() : ''))
      .filter((item) => item.length > 0);

    if (values.length === 0) return null;
    return Array.from(new Set(values)).join(', ');
  }

  return null;
};

const normalizeSummaryDeskNames = (row: KatanaPathSummary): KatanaPathSummary => {
  const currentDeskNames = normalizeDeskNameValue(row.desk_names);
  if (currentDeskNames) {
    return row;
  }

  const deskAliases = [
    row.nom_transport_desk,
    row.nom_transport_desks,
    row.desk_names,
    row.path_desks,
    row.transport_desk_names,
    row.desks,
  ];

  const fallbackDeskNames =
    deskAliases
      .map((value) => normalizeDeskNameValue(value))
      .find((value): value is string => Boolean(value)) ?? null;

  if (!fallbackDeskNames) {
    return row;
  }

  return {
    ...row,
    desk_names: fallbackDeskNames,
  };
};

const normalizeDumpResults = (
  results: Array<KatanaPathDumpRow | KatanaPathSummary>,
): Array<KatanaPathDumpRow | KatanaPathSummary> =>
  results.map((result) => (isRowResult(result) ? result : normalizeSummaryDeskNames(result)));

const normalizeDumpResponse = (
  response:
    | {
        count?: number;
        next?: string | null;
        previous?: string | null;
        results?: Array<KatanaPathDumpRow | KatanaPathSummary>;
      }
    | Array<KatanaPathDumpRow | KatanaPathSummary>
    | undefined,
) => {
  if (Array.isArray(response)) {
    const normalizedResults = normalizeDumpResults(response);
    return {
      count: normalizedResults.length,
      next: null,
      previous: null,
      results: normalizedResults,
    };
  }

  const normalizedResults = normalizeDumpResults(response?.results ?? []);

  return {
    count: response?.count ?? 0,
    next: response?.next ?? null,
    previous: response?.previous ?? null,
    results: normalizedResults,
  };
};

type SummaryTranslationStatus = {
  status: string;
  reasonCode: string | null;
  reasonMessage: string | null;
  deliveredDth: number | null;
  pathFingerprint: string | null;
  pathRecordId: number | null;
  missingPhysicalPointIds: number[];
  missingNodePointIds: number[];
};

type TranslationStatusLookup = {
  bySourcePathId: Record<number, SummaryTranslationStatus>;
  byGroupKey: Record<string, SummaryTranslationStatus>;
};

type TranslationPreviewDisplayRow = KatanaTranslationPreviewRow & {
  route_list?: string | null;
};

const buildTranslationPreviewBySourcePathId = (
  rows: KatanaTranslationPreviewRow[],
): Record<number, KatanaTranslationPreviewRow> => {
  const bySourcePathId: Record<number, KatanaTranslationPreviewRow> = {};

  rows.forEach((row) => {
    const sourcePathId =
      typeof row.source_path_id === 'number'
        ? row.source_path_id
        : typeof row.id === 'number'
        ? row.id
        : typeof row.katana_path_id === 'number'
        ? row.katana_path_id
        : null;

    if (sourcePathId !== null) {
      bySourcePathId[sourcePathId] = row;
    }
  });

  return bySourcePathId;
};

const normalizeTranslationResponse = (
  response:
    | {
        count?: number;
        next?: string | null;
        previous?: string | null;
        results?: KatanaTranslationPreviewRow[];
      }
    | KatanaTranslationPreviewRow[]
    | undefined,
) => {
  if (Array.isArray(response)) {
    return {
      count: response.length,
      next: null,
      previous: null,
      results: response,
    };
  }

  return {
    count: response?.count ?? 0,
    next: response?.next ?? null,
    previous: response?.previous ?? null,
    results: response?.results ?? [],
  };
};

const buildTranslationStatusLookup = (
  rows: KatanaTranslationPreviewRow[],
): TranslationStatusLookup => {
  const bySourcePathId: Record<number, SummaryTranslationStatus> = {};
  const byGroupKey: Record<string, SummaryTranslationStatus> = {};

  rows.forEach((row) => {
    const sourcePathId =
      typeof row.source_path_id === 'number'
        ? row.source_path_id
        : typeof row.id === 'number'
        ? row.id
        : typeof row.katana_path_id === 'number'
        ? row.katana_path_id
        : null;

    const details: SummaryTranslationStatus = {
      status: row.translation_status || row.status || 'Unknown',
      reasonCode: row.translation_reason_code ?? null,
      reasonMessage: row.translation_reason_message ?? null,
      deliveredDth: row.delivered_dth,
      pathFingerprint: row.path_fingerprint,
      pathRecordId: row.path_record_id,
      missingPhysicalPointIds: row.missing_physical_point_ids,
      missingNodePointIds: row.missing_node_point_ids,
    };

    if (sourcePathId !== null) {
      bySourcePathId[sourcePathId] = details;
    }

    if (row.translation_group_key) {
      byGroupKey[row.translation_group_key] = details;
    }
  });

  return {
    bySourcePathId,
    byGroupKey,
  };
};

const toNumericId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const getSummaryPathId = (row: KatanaPathSummary): number | null => {
  const sourcePathId = toNumericId(row.source_path_id);
  if (sourcePathId !== null) return sourcePathId;

  const rowId = toNumericId(row.id);
  if (rowId !== null) return rowId;
  return null;
};

const getDetailsPathId = (row: KatanaPathDetailsResponse): number | null => {
  const rowId = toNumericId(row.id);
  if (rowId !== null) return rowId;

  const sourcePathId = toNumericId(row.source_path_id);
  if (sourcePathId !== null) return sourcePathId;
  return null;
};

const getSummaryTranslationGroupKey = (row: KatanaPathSummary): string | null => {
  if (typeof row.translation_group_key === 'string' && row.translation_group_key.length > 0) {
    return row.translation_group_key;
  }
  return null;
};

const toErrorMessage = (value: unknown): string | null => {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => toErrorMessage(item))
      .filter((item): item is string => Boolean(item));
    return parts.length ? parts.join('; ') : null;
  }

  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .map(([key, fieldValue]) => {
        const fieldMessage = toErrorMessage(fieldValue);
        if (!fieldMessage) return null;
        if (key === 'non_field_errors' || key === '__all__') return fieldMessage;
        return `${key}: ${fieldMessage}`;
      })
      .filter((item): item is string => Boolean(item));
    return entries.length ? entries.join(' | ') : null;
  }

  return null;
};

export const KatanaExplorer: React.FC = () => {
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [loadedResults, setLoadedResults] = useState<
    Array<KatanaPathDumpRow | KatanaPathSummary>
  >([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingAll, setIsLoadingAll] = useState(false);
  const [translationStatusLookup, setTranslationStatusLookup] =
    useState<TranslationStatusLookup>({
      bySourcePathId: {},
      byGroupKey: {},
    });
  const [translationPreviewBySourcePathId, setTranslationPreviewBySourcePathId] =
    useState<Record<number, KatanaTranslationPreviewRow>>({});
  const [isTranslationLoading, setIsTranslationLoading] = useState(false);
  const [translationErrorMessage, setTranslationErrorMessage] = useState<string | null>(null);
  const [pathElementsByKey, setPathElementsByKey] = useState<
    Record<number, KatanaPathElementDetail[]>
  >({});
  const [pathElementsLoadingByKey, setPathElementsLoadingByKey] = useState<
    Record<number, boolean>
  >({});
  const [pathElementsErrorByKey, setPathElementsErrorByKey] = useState<
    Record<number, string | null>
  >({});
  const [displayedSourcePathIds, setDisplayedSourcePathIds] = useState<number[]>([]);
  const [loadPathDetails] = useLazyGetKatanaPathDetailsQuery();
  const [loadTranslationPreview] = useLazyGetKatanaTranslationPreviewQuery();

  const {
    startDateInput,
    setStartDateInput,
    endDateInput,
    setEndDateInput,
    limitInput,
    setLimitInput,
    hasAppliedFilters,
    offset,
    setOffset,
    clientError,
    katanaTspSearch,
    setKatanaTspSearch,
    katanaTspPageNumber,
    setKatanaTspPageNumber,
    useAllKatanaTspIds,
    setUseAllKatanaTspIds,
    selectedKatanaTspIds,
    selectedKatanaTspIdsSorted,
    receiptSelection,
    deliverySelection,
    activePointModal,
    pointModalSearch,
    pointModalPageNumber,
    setPointModalPageNumber,
    pointModalKatanaTspId,
    viewMode,
    setViewMode,
    appliedFilters,
    hiddenColumns,
    debouncedTspSearch,
    debouncedPointSearch,
    toggleTspSelection,
    togglePointSelection,
    clearSelectedPoints,
    openPointModal,
    closePointModal,
    updatePointModalSearch,
    updatePointModalPipeline,
    toggleAdvancedColumns,
    toggleColumn,
    onApplyFilters,
  } = useKatanaExplorerFilters();

  const shouldLoadBrowseTsp = !useAllKatanaTspIds || activePointModal !== null;
  const tspBrowseArgs = shouldLoadBrowseTsp
    ? {
        page: katanaTspPageNumber,
        search: debouncedTspSearch || undefined,
      }
    : skipToken;

  const { data: katanaTspPage } = useGetKatanaTSPsQuery(tspBrowseArgs);

  const selectedTspLookupArgs =
    !useAllKatanaTspIds &&
    selectedKatanaTspIdsSorted.length > 0 &&
    activePointModal !== null
      ? { id__in: selectedKatanaTspIdsSorted }
      : skipToken;

  const { data: selectedKatanaTspPage } =
    useGetKatanaTSPsQuery(selectedTspLookupArgs);

  const pointModalQueryArgs = useMemo(() => {
    if (!activePointModal) return skipToken;

    const base = {
      page: pointModalPageNumber,
      search: debouncedPointSearch || undefined,
    };

    if (pointModalKatanaTspId) {
      return {
        ...base,
        katana_tsp_id: pointModalKatanaTspId,
      };
    }

    if (!useAllKatanaTspIds && selectedKatanaTspIdsSorted.length > 0) {
      return {
        ...base,
        katana_tsp_id__in: selectedKatanaTspIdsSorted,
      };
    }

    return base;
  }, [
    activePointModal,
    pointModalPageNumber,
    debouncedPointSearch,
    pointModalKatanaTspId,
    useAllKatanaTspIds,
    selectedKatanaTspIdsSorted,
  ]);

  const { data: pointModalPage } = useGetKatanaPointsQuery(pointModalQueryArgs);

  const queryArgs = useMemo(() => {
    if (!hasAppliedFilters) return skipToken;
    return {
      ...appliedFilters,
      offset,
      limit: appliedFilters.limit ?? defaultLimit,
    };
  }, [hasAppliedFilters, appliedFilters, offset]);

  const {
    data,
    isLoading,
    isFetching,
    error,
  } = useGetKatanaPathDumpQuery(queryArgs);
  const normalizedData = useMemo(() => normalizeDumpResponse(data as any), [data]);

  const filterSignature = useMemo(() => {
    if (!hasAppliedFilters) return 'unapplied';
    return JSON.stringify(appliedFilters);
  }, [hasAppliedFilters, appliedFilters]);

  useEffect(() => {
    setLoadedResults([]);
    setTotalCount(0);
    setIsLoadingAll(false);
    setTranslationStatusLookup({
      bySourcePathId: {},
      byGroupKey: {},
    });
    setTranslationPreviewBySourcePathId({});
    setTranslationErrorMessage(null);
    setIsTranslationLoading(false);
    setPathElementsByKey({});
    setPathElementsLoadingByKey({});
    setPathElementsErrorByKey({});
    setDisplayedSourcePathIds([]);
  }, [filterSignature]);

  useEffect(() => {
    if (!hasAppliedFilters) return;

    let isCurrent = true;

    const loadAllTranslationStatuses = async () => {
      setTranslationErrorMessage(null);
      setIsTranslationLoading(true);

      try {
        const baseArgs = {
          start_date: appliedFilters.start_date,
          end_date: appliedFilters.end_date,
          tsp_ids: appliedFilters.tsp_ids,
          path_r_point_ids: appliedFilters.path_r_point_ids,
          path_d_point_ids: appliedFilters.path_d_point_ids,
          limit: 1000,
        };

        let offsetValue = 0;
        let allResults: KatanaTranslationPreviewRow[] = [];
        let expectedCount = 0;
        let hasNextPage = false;

        do {
          const response = await loadTranslationPreview(
            {
              ...baseArgs,
              offset: offsetValue,
            },
            true,
          ).unwrap();

          if (!isCurrent) return;

          const normalized = normalizeTranslationResponse(response);
          if (offsetValue === 0) {
            expectedCount = normalized.count;
          }

          allResults = [...allResults, ...normalized.results];
          offsetValue += normalized.results.length;
          hasNextPage = Boolean(normalized.next);
        } while (hasNextPage && allResults.length < expectedCount);

        if (!isCurrent) return;

        setTranslationStatusLookup(buildTranslationStatusLookup(allResults));
        setTranslationPreviewBySourcePathId(
          buildTranslationPreviewBySourcePathId(allResults),
        );
      } catch (err) {
        if (!isCurrent) return;

        const fallback =
          toErrorMessage((err as any)?.data) ||
          (typeof (err as any)?.error === 'string' ? (err as any).error : null) ||
          'Unable to load translation preview for this filter set.';
        setTranslationErrorMessage(fallback);
      } finally {
        if (isCurrent) {
          setIsTranslationLoading(false);
        }
      }
    };

    loadAllTranslationStatuses();

    return () => {
      isCurrent = false;
    };
  }, [hasAppliedFilters, filterSignature, appliedFilters, loadTranslationPreview]);

  useEffect(() => {
    if (!hasAppliedFilters) return;

    setTotalCount(normalizedData.count ?? 0);

    const incoming = normalizedData.results;

    setLoadedResults((prev) => {
      if (offset === 0) return incoming;
      return mergeUniqueResults(prev, incoming);
    });
  }, [hasAppliedFilters, normalizedData, offset]);

  const allKatanaTspEntries = useMemo(
    () =>
      (katanaTspPage?.results ?? [])
        .filter(Boolean)
        .sort((a: KatanaTSP, b: KatanaTSP) => a.name.localeCompare(b.name)),
    [katanaTspPage],
  );

  const selectedKatanaTspEntries = useMemo(
    () =>
      (selectedKatanaTspPage?.results ?? [])
        .filter(Boolean)
        .sort((a: KatanaTSP, b: KatanaTSP) => a.name.localeCompare(b.name)),
    [selectedKatanaTspPage],
  );

  const pipelineOptionsForPointModal = useMemo(() => {
    if (!useAllKatanaTspIds && selectedKatanaTspEntries.length > 0) {
      return selectedKatanaTspEntries;
    }
    return allKatanaTspEntries;
  }, [useAllKatanaTspIds, selectedKatanaTspEntries, allKatanaTspEntries]);

  const pointModalPipelineNameById = useMemo(() => {
    const nameById: Record<number, string> = {};
    pipelineOptionsForPointModal.forEach((tsp) => {
      nameById[tsp.id] = tsp.name;
    });
    return nameById;
  }, [pipelineOptionsForPointModal]);

  const visibleColumns = useMemo(
    () => columns.filter((column) => !hiddenColumns.has(column.key)),
    [hiddenColumns],
  );

  const apiErrorMessage = useMemo(() => {
    const err = error as any;
    if (!err) return null;

    if (typeof err?.data?.error === 'string') return err.data.error;
    if (typeof err?.data?.detail === 'string') return err.data.detail;
    const dataMessage = toErrorMessage(err?.data);
    if (dataMessage) return dataMessage;
    if (typeof err?.error === 'string') return err.error;

    return 'Request failed. Please verify filters and try again.';
  }, [error]);

  const activeView = hasAppliedFilters ? appliedFilters.view ?? 'summary' : viewMode;

  const count = totalCount;
  const rowResults = useMemo(
    () => (activeView === 'rows' ? loadedResults.filter(isRowResult) : []),
    [activeView, loadedResults],
  );
  const summaryResults = useMemo(
    () => (activeView === 'rows' ? [] : loadedResults.filter(isSummaryResult)),
    [activeView, loadedResults],
  );
  const loadedCount = activeView === 'rows' ? rowResults.length : summaryResults.length;
  const routeBySourcePathId = useMemo(() => {
    const normalizeRouteText = (value: unknown): string | null => {
      if (typeof value === 'string') {
        const trimmed = value.trim();
        return trimmed.length > 0 ? trimmed : null;
      }

      if (Array.isArray(value)) {
        const parts = value
          .map((item) => (typeof item === 'string' ? item.trim() : ''))
          .filter((item) => item.length > 0);
        return parts.length > 0 ? parts.join(' | ') : null;
      }

      return null;
    };

    const routeLookup: Record<number, string> = {};

    loadedResults.forEach((result) => {
      if (isRowResult(result)) {
        const sourcePathId =
          toNumericId(result.source_path_id) ??
          toNumericId(result.id) ??
          toNumericId(result.path_id);

        if (sourcePathId === null || routeLookup[sourcePathId]) return;

        const routeText = normalizeRouteText(result.route_list || result.point_names);
        if (routeText) {
          routeLookup[sourcePathId] = routeText;
        }
        return;
      }

      const sourcePathId = getSummaryPathId(result);
      if (sourcePathId === null || routeLookup[sourcePathId]) return;

      const routeText =
        normalizeRouteText(result.route_list) ||
        normalizeRouteText(result.original_path_key) ||
        normalizeRouteText(result.original_path_fingerprint);

      if (routeText) {
        routeLookup[sourcePathId] = routeText;
      }
    });

    return routeLookup;
  }, [loadedResults]);

  const displayedTranslationPreviewRows: TranslationPreviewDisplayRow[] = useMemo(
    () => {
      return displayedSourcePathIds.reduce<TranslationPreviewDisplayRow[]>(
        (acc, sourcePathId) => {
          const row = translationPreviewBySourcePathId[sourcePathId];
          if (!row) return acc;

          acc.push({
            ...row,
            route_list: routeBySourcePathId[sourcePathId] ?? null,
          });
          return acc;
        },
        [],
      );
    },
    [
      displayedSourcePathIds,
      translationPreviewBySourcePathId,
      routeBySourcePathId,
    ],
  );
  const activeLimit = appliedFilters.limit ?? defaultLimit;
  const canNext = hasAppliedFilters && loadedCount < count;
  const fromRow = count === 0 ? 0 : 1;
  const toRow = Math.min(loadedCount, count);
  const resultNoun = activeView === 'rows' ? 'Rows' : 'Paths';
  const loadNextPage = useCallback(() => {
    if (!hasAppliedFilters || isFetching || !canNext) return;
    setOffset((prevOffset) => prevOffset + activeLimit);
  }, [hasAppliedFilters, isFetching, canNext, setOffset, activeLimit]);

  useEffect(() => {
    if (!isLoadingAll) return;
    if (!hasAppliedFilters) {
      setIsLoadingAll(false);
      return;
    }
    if (isFetching) return;
    if (!canNext) {
      setIsLoadingAll(false);
      return;
    }

    setOffset((prevOffset) => prevOffset + activeLimit);
  }, [
    isLoadingAll,
    hasAppliedFilters,
    isFetching,
    canNext,
    setOffset,
    activeLimit,
  ]);

  const onLoadAll = useCallback(() => {
    if (!hasAppliedFilters || isFetching || !canNext) return;

    const confirmed = window.confirm(
      'Load all results? This may take some time and can impact browser performance for very large datasets.',
    );
    if (!confirmed) return;

    setIsLoadingAll(true);
  }, [hasAppliedFilters, isFetching, canNext]);

  const resolveSummaryTranslationStatus = useCallback(
    (row: KatanaPathSummary): SummaryTranslationStatus | null => {
      const pathId = getSummaryPathId(row);

      if (pathId !== null && translationStatusLookup.bySourcePathId[pathId]) {
        return translationStatusLookup.bySourcePathId[pathId];
      }

      const groupKey = getSummaryTranslationGroupKey(row);
      if (groupKey && translationStatusLookup.byGroupKey[groupKey]) {
        return translationStatusLookup.byGroupKey[groupKey];
      }

      if (row.translation_status || row.translation_reason_code || row.translation_reason_message) {
        return {
          status: row.translation_status ?? 'Unknown',
          reasonCode: row.translation_reason_code ?? null,
          reasonMessage: row.translation_reason_message ?? null,
          deliveredDth: null,
          pathFingerprint: row.original_path_fingerprint ?? null,
          pathRecordId: row.path_record_id ?? null,
          missingPhysicalPointIds: row.missing_physical_point_ids ?? [],
          missingNodePointIds: row.missing_node_point_ids ?? [],
        };
      }

      return null;
    },
    [translationStatusLookup],
  );

  const resolvePathElementsForSummary = useCallback(
    (row: KatanaPathSummary): KatanaPathElementDetail[] => {
      if (Array.isArray(row.path_elements) && row.path_elements.length > 0) {
        return row.path_elements;
      }

      const pathId = getSummaryPathId(row);
      if (pathId !== null && pathElementsByKey[pathId]) {
        return pathElementsByKey[pathId];
      }

      return [];
    },
    [pathElementsByKey],
  );

  const isPathElementsLoadingForSummary = useCallback(
    (row: KatanaPathSummary): boolean => {
      const pathId = getSummaryPathId(row);
      if (pathId === null) return false;
      return Boolean(pathElementsLoadingByKey[pathId]);
    },
    [pathElementsLoadingByKey],
  );

  const resolvePathElementsErrorForSummary = useCallback(
    (row: KatanaPathSummary): string | null => {
      const pathId = getSummaryPathId(row);
      if (pathId === null) {
        if (Array.isArray(row.path_elements) && row.path_elements.length > 0) {
          return null;
        }
        return 'No source_path_id/id was provided for this row. Path elements cannot be loaded.';
      }

      return pathElementsErrorByKey[pathId] ?? null;
    },
    [pathElementsErrorByKey],
  );

  const requestPathElementsForSummary = useCallback(
    async (row: KatanaPathSummary) => {
      if (Array.isArray(row.path_elements) && row.path_elements.length > 0) {
        return;
      }

      const pathId = getSummaryPathId(row);
      if (pathId === null) {
        return;
      }

      if (pathElementsLoadingByKey[pathId] || pathId in pathElementsByKey) {
        return;
      }

      setPathElementsLoadingByKey((prev) => {
        return {
          ...prev,
          [pathId]: true,
        };
      });

      setPathElementsErrorByKey((prev) => {
        return {
          ...prev,
          [pathId]: null,
        };
      });

      try {
        const response = await loadPathDetails(
          {
            id: pathId,
          },
          true,
        ).unwrap();

        const elements =
          Array.isArray(response.path_elements) && response.path_elements.length > 0
            ? response.path_elements
            : [];

        const detailsPathId = getDetailsPathId(response);
        const statusPayload = response.translation_details;
        const statusSourcePathId =
          typeof statusPayload?.source_path_id === 'number'
            ? statusPayload.source_path_id
            : detailsPathId;
        const statusGroupKey = statusPayload?.translation_group_key;

        setPathElementsByKey((prev) => {
          return {
            ...prev,
            [pathId]: elements,
          };
        });

        if (statusPayload) {
          setTranslationStatusLookup((prev) => {
            const nextBySourcePathId = { ...prev.bySourcePathId };
            const nextByGroupKey = { ...prev.byGroupKey };

            const previousStatus =
              (statusSourcePathId !== null && statusSourcePathId !== undefined
                ? prev.bySourcePathId[statusSourcePathId]
                : null) ||
              (statusGroupKey ? prev.byGroupKey[statusGroupKey] : null);

            const nextStatus: SummaryTranslationStatus = {
              status:
                statusPayload.translation_status ||
                statusPayload.status ||
                previousStatus?.status ||
                'Unknown',
              reasonCode: statusPayload.translation_reason_code ?? null,
              reasonMessage: statusPayload.translation_reason_message ?? null,
              deliveredDth: statusPayload.delivered_dth ?? null,
              pathFingerprint:
                statusPayload.path_fingerprint ?? response.path_fingerprint ?? null,
              pathRecordId: statusPayload.path_record_id ?? null,
              missingPhysicalPointIds: statusPayload.missing_physical_point_ids ?? [],
              missingNodePointIds: statusPayload.missing_node_point_ids ?? [],
            };

            if (typeof statusSourcePathId === 'number') {
              nextBySourcePathId[statusSourcePathId] = nextStatus;
            }

            if (statusGroupKey) {
              nextByGroupKey[statusGroupKey] = nextStatus;
            }

            return {
              bySourcePathId: nextBySourcePathId,
              byGroupKey: nextByGroupKey,
            };
          });
        }

        if (elements.length === 0) {
          setPathElementsErrorByKey((prev) => ({
            ...prev,
            [pathId]: 'No path elements were returned for this path.',
          }));
        }
      } catch (err) {
        const message =
          toErrorMessage((err as any)?.data) ||
          (typeof (err as any)?.error === 'string' ? (err as any).error : null) ||
          'Unable to load path elements for this row.';

        setPathElementsErrorByKey((prev) => ({
          ...prev,
          [pathId]: message,
        }));
      } finally {
        setPathElementsLoadingByKey((prev) => ({
          ...prev,
          [pathId]: false,
        }));
      }
    },
    [loadPathDetails, pathElementsLoadingByKey, pathElementsByKey],
  );

  return (
    <div className="container-fluid py-3 px-4">
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <h4 className="mb-1">Katana Explorer</h4>
        </div>
      </div>

      <form className="card card-body mb-3" onSubmit={onApplyFilters}>
        <div className="d-flex flex-column gap-3">
          <div>
            <h6 className="mb-2">Base Filters</h6>
            <div className="row g-2">
              <div className="col-12">
                <label className="form-label mb-1">Start Date</label>
                <input
                  className="form-control"
                  type="date"
                  value={startDateInput}
                  onChange={(evt) => setStartDateInput(evt.target.value)}
                  onBlur={() => {
                    if (!endDateInput && startDateInput) {
                      setEndDateInput(startDateInput);
                    }
                  }}
                />
                <small className="text-muted">
                  Defaults to today. Clear this field to remove the start-date filter.
                </small>
              </div>

              <div className="col-12">
                <label className="form-label mb-1">End Date</label>
                <input
                  className="form-control"
                  type="date"
                  value={endDateInput}
                  onChange={(evt) => setEndDateInput(evt.target.value)}
                  placeholder="Defaults to start date"
                />
              </div>

              <div className="col-12">
                <label className="form-label mb-1">View Mode</label>
                <select
                  className="form-select"
                  value={viewMode}
                  onChange={(evt) => setViewMode(evt.target.value as KatanaPathDumpView)}
                >
                  {viewModeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <small className="text-muted d-block mt-1">
                  {
                    viewModeOptions.find((option) => option.value === viewMode)
                      ?.helpText
                  }
                </small>
              </div>
            </div>
          </div>

          <div>
            <h6 className="mb-2">Katana TSPs</h6>
            <div className="form-check form-switch mb-2">
              <input
                className="form-check-input"
                id="katana-all-tsps-switch"
                type="checkbox"
                checked={useAllKatanaTspIds}
                onChange={(evt) => setUseAllKatanaTspIds(evt.target.checked)}
              />
              <label className="form-check-label" htmlFor="katana-all-tsps-switch">
                All TSPs (default)
              </label>
            </div>

            <div className="border rounded p-2">
              {!useAllKatanaTspIds && (
                <>
                  <div className="fw-semibold mb-1">Select TSPs By Name</div>
                  <input
                    className="form-control form-control-sm mb-2"
                    type="text"
                    value={katanaTspSearch}
                    onChange={(evt) => {
                      setKatanaTspSearch(evt.target.value);
                      setKatanaTspPageNumber(1);
                    }}
                    placeholder="Search pipeline name"
                  />
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">
                      Selected pipelines: {selectedKatanaTspIds.length}
                    </small>
                    <div className="btn-group">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        disabled={!katanaTspPage?.previous}
                        onClick={() =>
                          setKatanaTspPageNumber((prev) => Math.max(1, prev - 1))
                        }
                      >
                        Prev
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        disabled={!katanaTspPage?.next}
                        onClick={() => setKatanaTspPageNumber((prev) => prev + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                  <div style={{ maxHeight: 180, overflowY: 'auto' }}>
                    {(katanaTspPage?.results ?? []).map((tsp) => (
                      <label key={tsp.id} className="d-block small">
                        <input
                          type="checkbox"
                          className="form-check-input me-2"
                          checked={selectedKatanaTspIds.includes(tsp.id)}
                          onChange={() => toggleTspSelection(tsp.id)}
                        />
                        {tsp.name}
                      </label>
                    ))}
                  </div>
                </>
              )}

              {useAllKatanaTspIds && (
                <small className="text-muted d-block">
                  All pipelines are included. Disable "All TSPs" to pick specific
                  pipelines by name.
                </small>
              )}
            </div>
          </div>

          <div>
            <h6 className="mb-2">Points</h6>
            <div className="d-flex flex-column gap-2">
              <PointSummary
                title="Receipt Points"
                emptyLabel="No receipt points selected."
                selection={receiptSelection}
                onAdd={() => openPointModal('receipt')}
                onClear={() => clearSelectedPoints('receipt')}
              />

              <PointSummary
                title="Delivery Points"
                emptyLabel="No delivery points selected."
                selection={deliverySelection}
                onAdd={() => openPointModal('delivery')}
                onClear={() => clearSelectedPoints('delivery')}
              />
            </div>
          </div>

          <ColumnSettings
            hiddenColumns={hiddenColumns}
            onToggleColumn={toggleColumn}
            onToggleAdvancedColumns={toggleAdvancedColumns}
            showColumnControls={viewMode === 'rows'}
            limitInput={limitInput}
            onLimitChange={setLimitInput}
          />

          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              Katana TSP count: {katanaTspPage?.count ?? allKatanaTspEntries.length}
            </small>
            <div className="d-flex gap-2">
              <button type="submit" className="btn btn-primary">
                Get Paths
              </button>
            </div>
          </div>

          {clientError && <div className="alert alert-danger mb-0">{clientError}</div>}
          {apiErrorMessage && (
            <div className="alert alert-danger mb-0">{apiErrorMessage}</div>
          )}
        </div>
      </form>

      <PointSelectionModal
        isOpen={activePointModal !== null}
        mode={activePointModal}
        onClose={closePointModal}
        pointSearch={pointModalSearch}
        onPointSearchChange={updatePointModalSearch}
        onPrevPage={() => setPointModalPageNumber((prev) => Math.max(1, prev - 1))}
        onNextPage={() => setPointModalPageNumber((prev) => prev + 1)}
        pageHasPrev={Boolean(pointModalPage?.previous)}
        pageHasNext={Boolean(pointModalPage?.next)}
        pointResults={pointModalPage?.results ?? []}
        pointPipelineId={pointModalKatanaTspId}
        onPointPipelineIdChange={updatePointModalPipeline}
        pipelineOptions={pipelineOptionsForPointModal}
        pipelineNameById={pointModalPipelineNameById}
        selectedReceiptIds={receiptSelection.ids}
        selectedDeliveryIds={deliverySelection.ids}
        onToggleReceiptPoint={(point) => togglePointSelection('receipt', point)}
        onToggleDeliveryPoint={(point) => togglePointSelection('delivery', point)}
      />

      <ExportToPathReportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        hasAppliedFilters={hasAppliedFilters}
        sourcePathIds={displayedSourcePathIds}
      />

      <TranslationPreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        sourcePathIds={displayedSourcePathIds}
        rows={displayedTranslationPreviewRows}
        isLoading={isTranslationLoading}
        errorMessage={translationErrorMessage}
      />

      <BelowMapSetter>
        <BelowMapTab
          tabName="Katana Explorer"
          contextKey="katana-explorer"
          tabOrder={0}
          activeTab={true}
        >
          <div className="h-100 d-flex flex-column p-3 gap-3">
            <div className="card card-body flex-grow-1">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <div className="text-muted">
                  {!hasAppliedFilters
                    ? 'Set filters and click Get Paths to load results'
                    : isLoading
                    ? 'Loading...'
                    : `${resultNoun} ${fromRow}-${toRow} of ${count}`}
                  {hasAppliedFilters && !isLoading && canNext && (
                    <span className="ms-2">Scroll to load more.</span>
                  )}
                </div>

                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    disabled={!hasAppliedFilters || displayedSourcePathIds.length === 0}
                    onClick={() => setIsPreviewModalOpen(true)}
                    title="Preview translations for currently displayed table paths"
                  >
                    Translation Preview
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-success btn-sm"
                    disabled={!hasAppliedFilters || displayedSourcePathIds.length === 0}
                    onClick={() => setIsExportModalOpen(true)}
                    title="Export currently displayed table paths to Path Report"
                  >
                    Export to Path Report
                  </button>
                  <button
                    className="btn btn-primary btn-sm"
                    disabled={!canNext || !hasAppliedFilters || isFetching || isLoadingAll}
                    onClick={onLoadAll}
                    title="Load all remaining results (can take time)"
                  >
                    {isLoadingAll ? 'Loading All...' : 'Load All'}
                  </button>
                </div>
              </div>

              {activeView === 'rows' ? (
                <ResultsTable
                  hasAppliedFilters={hasAppliedFilters}
                  isLoading={isLoading}
                  isFetching={isFetching}
                  visibleColumns={visibleColumns}
                  results={rowResults}
                  canLoadMore={canNext}
                  onLoadMore={loadNextPage}
                  onDisplayedSourcePathIdsChange={setDisplayedSourcePathIds}
                />
              ) : (
                <PathSummaryResults
                  hasAppliedFilters={hasAppliedFilters}
                  isLoading={isLoading}
                  isFetching={isFetching}
                  mode={activeView}
                  results={summaryResults}
                  canLoadMore={canNext}
                  onLoadMore={loadNextPage}
                  isTranslationLoading={isTranslationLoading}
                  translationErrorMessage={translationErrorMessage}
                  resolveTranslationStatus={resolveSummaryTranslationStatus}
                  onRequestPathElements={requestPathElementsForSummary}
                  resolvePathElements={resolvePathElementsForSummary}
                  isPathElementsLoading={isPathElementsLoadingForSummary}
                  resolvePathElementsError={resolvePathElementsErrorForSummary}
                  onDisplayedSourcePathIdsChange={setDisplayedSourcePathIds}
                />
              )}
            </div>
          </div>
        </BelowMapTab>
        <BelowMapTab
          tabName="Pathing Status"
          contextKey="pathing-status"
          tabOrder={1}
          activeTab={false}
        >
          <PathingStatusTab />
        </BelowMapTab>
      </BelowMapSetter>
    </div>
  );
};
